import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  TextInput,
  Image,
  ActivityIndicator,
  FlatList,
  Linking,
  Dimensions,
  Alert,
  Modal,
  Share,
  Platform,
  Animated,
} from 'react-native';
import { 
  Ionicons, 
  MaterialCommunityIcons, 
  FontAwesome5,
  AntDesign,
  Feather,
} from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { BlurView } from 'expo-blur';

// Import unified data for company information
import { UNIFIED_JOB_DATA } from '../../services/UnifiedJobData';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');


export default function CompanyInfoScreen({ navigation, route }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // Extract company data from route params
  const { company: routeCompany, source } = route.params || {};

  // State management
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [relatedJobs, setRelatedJobs] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [companyAnalytics, setCompanyAnalytics] = useState({});
  const [showShareModal, setShowShareModal] = useState(false);
  const [filteredCompanies, setFilteredCompanies] = useState([]);

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;
  const scaleAnim = useRef(new Animated.Value(0.9)).current;
  const rotateAnim = useRef(new Animated.Value(0)).current;

  // Modern tab options with gradients
  const tabOptions = [
    { key: 'overview', label: 'Overview', icon: 'information-circle', gradient: ['#667eea', '#764ba2'] },
    { key: 'jobs', label: 'Jobs', icon: 'briefcase', gradient: ['#4facfe', '#00f2fe'] },
    { key: 'analytics', label: 'Analytics', icon: 'analytics', gradient: ['#43e97b', '#38f9d7'] },
    { key: 'reviews', label: 'Reviews', icon: 'star', gradient: ['#f093fb', '#f5576c'] },
    { key: 'culture', label: 'Culture', icon: 'people', gradient: ['#ffecd2', '#fcb69f'] },
  ];

  // Enhanced company data with modern design elements
  const companiesData = [
    {
      id: '1',
      name: 'TechCorp London',
      logo: 'https://via.placeholder.com/100/667eea/FFFFFF?text=TC',
      industry: 'Technology',
      location: 'London, UK',
      size: '500-1000 employees',
      founded: '2015',
      website: 'https://www.techcorp-london.com',
      description: 'TechCorp London is a leading technology company specializing in innovative software solutions for enterprise clients. We focus on cutting-edge technologies including AI, machine learning, and cloud computing.',
      overview: 'TechCorp London has established itself as a premier technology company in the UK market. With a focus on innovation and client satisfaction, we deliver world-class software solutions that drive business transformation.',
      culture: 'Our culture is built on collaboration, innovation, and continuous learning. We believe in empowering our employees to take ownership of their work and contribute to meaningful projects that make a real impact.',
      benefits: [
        'Competitive salary with performance bonuses',
        'Comprehensive health and dental insurance',
        'Flexible working arrangements and remote work options',
        'Professional development budget (£2,000 annually)',
        'Stock options and equity participation',
        '25 days annual leave plus bank holidays',
        'Modern office with state-of-the-art equipment',
        'Regular team building events and social activities',
      ],
      values: [
        'Innovation and creativity in problem-solving',
        'Collaboration and teamwork across all levels',
        'Continuous learning and professional growth',
        'Diversity and inclusion in the workplace',
        'Customer-centric approach to all projects',
      ],
      technologies: [
        'React', 'Node.js', 'Python', 'AWS', 'Docker', 'Kubernetes',
        'TypeScript', 'GraphQL', 'MongoDB', 'PostgreSQL'
      ],
      reviews: [
        {
          id: '1',
          rating: 4.5,
          title: 'Excellent place for career growth',
          comment: 'Amazing work environment with opportunities to work on cutting-edge technology. Great work-life balance and supportive management. The learning opportunities are endless.',
          date: '2025-03-15',
          position: 'Senior Software Engineer',
          pros: ['Great work-life balance', 'Learning opportunities', 'Modern tech stack'],
          cons: ['Fast-paced environment', 'High expectations'],
        },
        {
          id: '2',
          rating: 4.2,
          title: 'Innovative and challenging',
          comment: 'Complex projects that push your technical abilities. Great colleagues and collaborative atmosphere. The company really invests in employee development.',
          date: '2025-02-22',
          position: 'Product Manager',
          pros: ['Challenging projects', 'Great team', 'Investment in development'],
          cons: ['Sometimes demanding deadlines'],
        },
        {
          id: '3',
          rating: 4.0,
          title: 'Good benefits and growth potential',
          comment: 'The benefits package is excellent, and there are real opportunities for advancement. The company culture is very supportive of innovation.',
          date: '2025-01-10',
          position: 'UX Designer',
          pros: ['Excellent benefits', 'Growth opportunities', 'Supportive culture'],
          cons: ['Office can be noisy', 'Parking is limited'],
        },
      ],
      rating: 4.2,
      openPositions: 12,
      salaryRange: '£45,000 - £85,000',
      headquarters: 'London, UK',
      employees: '500-1000',
      founded: '2015',
      revenue: '£50M - £100M',
      gradient: ['#667eea', '#764ba2'],
    },
    {
      id: '2',
      name: 'StartupHub Manchester',
      logo: 'https://via.placeholder.com/100/4facfe/FFFFFF?text=SH',
      industry: 'Fintech',
      location: 'Manchester, UK',
      size: '100-500 employees',
      founded: '2018',
      website: 'https://www.startuphub-manchester.com',
      description: 'StartupHub Manchester is a rapidly growing fintech company revolutionizing digital banking and payment solutions. We combine innovative technology with user-centric design.',
      overview: 'As one of Manchester\'s fastest-growing fintech companies, StartupHub is transforming how people interact with financial services through innovative digital solutions.',
      culture: 'We foster a startup mentality with the stability of a growing company. Our culture emphasizes agility, innovation, and making a real impact in the financial technology space.',
      benefits: [
        'Competitive salary with equity options',
        'Health and wellness programs',
        'Flexible working hours and remote work',
        'Learning and development opportunities',
        '23 days annual leave',
        'Modern office in Manchester city center',
        'Regular company events and team outings',
      ],
      values: [
        'Innovation in financial technology',
        'Customer-first approach',
        'Agility and adaptability',
        'Transparency and trust',
        'Continuous improvement',
      ],
      technologies: [
        'React Native', 'Node.js', 'Python', 'AWS', 'Blockchain',
        'TypeScript', 'REST APIs', 'PostgreSQL', 'Redis'
      ],
      reviews: [
        {
          id: '1',
          rating: 4.3,
          title: 'Great startup environment',
          comment: 'Fast-paced and exciting work environment. Lots of opportunities to learn and grow. The team is very collaborative and supportive.',
          date: '2025-03-10',
          position: 'Frontend Developer',
          pros: ['Fast-paced environment', 'Learning opportunities', 'Great team'],
          cons: ['Can be stressful at times', 'Rapid changes'],
        },
        {
          id: '2',
          rating: 4.1,
          title: 'Innovative fintech company',
          comment: 'Working on cutting-edge fintech solutions. The company culture is very open and encourages innovation. Good work-life balance.',
          date: '2025-02-18',
          position: 'Backend Developer',
          pros: ['Innovative projects', 'Open culture', 'Work-life balance'],
          cons: ['Limited resources sometimes'],
        },
      ],
      rating: 4.2,
      openPositions: 8,
      salaryRange: '£40,000 - £75,000',
      headquarters: 'Manchester, UK',
      employees: '100-500',
      founded: '2018',
      revenue: '£10M - £50M',
      gradient: ['#4facfe', '#00f2fe'],
    },
    {
      id: '3',
      name: 'FinTech Solutions Edinburgh',
      logo: 'https://via.placeholder.com/100/43e97b/FFFFFF?text=FS',
      industry: 'Financial Services',
      location: 'Edinburgh, UK',
      size: '1000+ employees',
      founded: '2010',
      website: 'https://www.fintech-solutions-edinburgh.com',
      description: 'FinTech Solutions Edinburgh is a leading financial services company providing innovative solutions for banking, insurance, and investment management.',
      overview: 'With over a decade of experience, FinTech Solutions Edinburgh has become a trusted partner for financial institutions across the UK and Europe.',
      culture: 'We maintain a professional yet collaborative environment that values expertise, integrity, and innovation in financial services.',
      benefits: [
        'Competitive salary and bonus structure',
        'Comprehensive benefits package',
        'Professional development programs',
        'Pension scheme with company matching',
        '25 days annual leave',
        'Flexible working arrangements',
        'Health and wellness programs',
      ],
      values: [
        'Integrity and trust',
        'Excellence in service delivery',
        'Innovation in financial solutions',
        'Professional development',
        'Client satisfaction',
      ],
      technologies: [
        'Java', 'Spring Boot', 'Angular', 'Oracle', 'Kafka',
        'Microservices', 'REST APIs', 'Jenkins', 'Docker'
      ],
      reviews: [
        {
          id: '1',
          rating: 4.0,
          title: 'Stable and professional environment',
          comment: 'Great place to work with excellent benefits and job security. The work is challenging and the team is very professional.',
          date: '2025-03-05',
          position: 'Senior Analyst',
          pros: ['Job security', 'Excellent benefits', 'Professional environment'],
          cons: ['Can be slow to adopt new technologies'],
        },
      ],
      rating: 4.0,
      openPositions: 15,
      salaryRange: '£50,000 - £90,000',
      headquarters: 'Edinburgh, UK',
      employees: '1000+',
      founded: '2010',
      revenue: '£100M+',
      gradient: ['#43e97b', '#38f9d7'],
    },
  ];

  // Initialize component
  useEffect(() => {
    initializeCompanyData();
    startAnimations();
  }, []);

  // Focus effect to refresh data
  useFocusEffect(
    useCallback(() => {
      if (routeCompany) {
        loadCompanyFromRoute();
      }
    }, [routeCompany])
  );

  // Enhanced entrance animations
  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }),
      Animated.spring(slideAnim, {
        toValue: 0,
        tension: 50,
        friction: 8,
        useNativeDriver: true,
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        tension: 50,
        friction: 8,
        useNativeDriver: true,
      }),
    ]).start();

    // Continuous rotation for loading indicators
    Animated.loop(
      Animated.timing(rotateAnim, {
        toValue: 1,
        duration: 2000,
        useNativeDriver: true,
      })
    ).start();
  };

  // Initialize company data
  const initializeCompanyData = async () => {
    try {
      setIsLoading(true);
      
      // Generate company analytics from unified job data
      const analytics = generateCompanyAnalytics();
      setCompanyAnalytics(analytics);
      
      // Set filtered companies
      setFilteredCompanies(companiesData);
      
      // If company passed from route, select it
      if (routeCompany) {
        loadCompanyFromRoute();
      }
      
    } catch (error) {
      console.error('❌ Error initializing company data:', error);
      Alert.alert('Error', 'Failed to load company information.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load company from route parameters
  const loadCompanyFromRoute = () => {
    try {
      let companyToSelect = null;
      
      if (typeof routeCompany === 'string') {
        // Find company by name
        companyToSelect = companiesData.find(c => 
          String(c.name ?? '').toLowerCase().includes(routeCompany.toLowerCase()) ||
          routeCompany.toLowerCase().includes(String(c.name ?? '').toLowerCase())
        );
      } else if (routeCompany && routeCompany.company) {
        // Find company by company property
        companyToSelect = companiesData.find(c => 
          String(c.name ?? '').toLowerCase().includes(String(routeCompany.company ?? '').toLowerCase()) ||
          String(routeCompany.company ?? '').toLowerCase().includes(String(c.name ?? '').toLowerCase())
        );
      }
      
      if (companyToSelect) {
        setSelectedCompany(companyToSelect);
        loadCompanyDetails(companyToSelect);
      } else {
        // Create a basic company profile from route data
        const basicCompany = {
          id: 'route_company',
          name: routeCompany.company || routeCompany,
          logo: routeCompany.logo || 'https://via.placeholder.com/100/667eea/FFFFFF?text=C',
          industry: 'Technology',
          location: 'UK',
          size: 'Unknown',
          founded: 'Unknown',
          website: `https://${(routeCompany.company || routeCompany).toLowerCase().replace(/\s+/g, '')}.com`,
          description: `${routeCompany.company || routeCompany} is a company in our network.`,
          overview: `Learn more about ${routeCompany.company || routeCompany} and their opportunities.`,
          culture: 'Information about company culture will be updated soon.',
          benefits: ['Competitive salary', 'Professional development', 'Team collaboration'],
          values: ['Innovation', 'Excellence', 'Teamwork'],
          technologies: ['Various technologies'],
          reviews: [],
          rating: 4.0,
          openPositions: routeCompany.count || 0,
          salaryRange: 'Competitive',
          headquarters: 'UK',
          employees: 'Unknown',
          revenue: 'Unknown',
          gradient: ['#667eea', '#764ba2'],
        };
        
        setSelectedCompany(basicCompany);
        loadCompanyDetails(basicCompany);
      }
    } catch (error) {
      console.error('❌ Error loading company from route:', error);
    }
  };

  // Generate company analytics from unified job data
  const generateCompanyAnalytics = () => {
    const analytics = {};
    
    UNIFIED_JOB_DATA.forEach(job => {
      const company = job.company;
      
      if (!analytics[company]) {
        analytics[company] = {
          totalApplications: 0,
          interviews: 0,
          offers: 0,
          rejections: 0,
          pending: 0,
          avgMatchScore: 0,
          positions: [],
          salaryRange: { min: null, max: null },
          responseRate: 0,
          successRate: 0,
        };
      }
      
      const stats = analytics[company];
      stats.totalApplications++;
      stats.positions.push(job.title);
      
      // Parse salary for range calculation
      if (job.salary) {
        const salaryMatch = job.salary.match(/£([\d,]+)/g);
        if (salaryMatch) {
          const salaries = salaryMatch.map(s => parseInt(s.replace(/[£,]/g, '')));
          if (stats.salaryRange.min === null || salaries[0] < stats.salaryRange.min) {
            stats.salaryRange.min = salaries[0];
          }
          if (stats.salaryRange.max === null || salaries[salaries.length - 1] > stats.salaryRange.max) {
            stats.salaryRange.max = salaries[salaries.length - 1];
          }
        }
      }
      
      // Count status-based metrics
      switch (job.status) {
        case 'interview_scheduled':
        case 'interview_completed':
          stats.interviews++;
          break;
        case 'offer_received':
          stats.offers++;
          break;
        case 'rejected':
          stats.rejections++;
          break;
        default:
          stats.pending++;
          break;
      }
      
      stats.avgMatchScore += job.matchScore || 0;
    });
    
    // Calculate rates
    Object.keys(analytics).forEach(company => {
      const stats = analytics[company];
      stats.avgMatchScore = Math.round(stats.avgMatchScore / stats.totalApplications);
      stats.successRate = stats.totalApplications > 0 ? 
        Math.round(((stats.interviews + stats.offers) / stats.totalApplications) * 100) : 0;
      stats.responseRate = stats.totalApplications > 0 ? 
        Math.round(((stats.totalApplications - stats.pending) / stats.totalApplications) * 100) : 0;
    });
    
    return analytics;
  };

  // Load company details
  const loadCompanyDetails = async (company) => {
    try {
      setCompanyDetails(company);
      
      // Load related jobs from unified data
      const jobs = UNIFIED_JOB_DATA.filter(job => 
        String(job.company ?? '').toLowerCase().includes(String(company.name ?? '').toLowerCase()) ||
        String(company.name ?? '').toLowerCase().includes(String(job.company ?? '').toLowerCase())
      );
      
      setRelatedJobs(jobs);
      
    } catch (error) {
      console.error('❌ Error loading company details:', error);
    }
  };

  // Handle search with animation
  const handleSearch = (query) => {
    setSearchQuery(query);
    
    Animated.timing(scaleAnim, {
      toValue: 0.98,
      duration: 150,
      useNativeDriver: true,
    }).start(() => {
      if (query.trim() === '') {
        setFilteredCompanies(companiesData);
      } else {
        const filtered = companiesData.filter(company =>
          String(company.name ?? '').toLowerCase().includes(query.toLowerCase()) ||
          String(company.industry ?? '').toLowerCase().includes(query.toLowerCase()) ||
          String(company.location ?? '').toLowerCase().includes(query.toLowerCase())
        );
        setFilteredCompanies(filtered);
      }
      
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 150,
        useNativeDriver: true,
      }).start();
    });
  };

  // Handle company selection with animation
  const handleCompanySelect = (company) => {
    Animated.sequence([
      Animated.timing(fadeAnim, { toValue: 0.7, duration: 200, useNativeDriver: true }),
      Animated.timing(fadeAnim, { toValue: 1, duration: 200, useNativeDriver: true }),
    ]).start();
    
    setSelectedCompany(company);
    loadCompanyDetails(company);
    setActiveTab('overview');
  };

  // Handle tab change with animation
  const handleTabChange = (tab) => {
    Animated.sequence([
      Animated.timing(slideAnim, { toValue: 20, duration: 150, useNativeDriver: true }),
      Animated.timing(slideAnim, { toValue: 0, duration: 150, useNativeDriver: true }),
    ]).start();
    
    setActiveTab(tab);
  };

  // Handle job application
  const handleJobApplication = (job) => {
    try {
      navigation.navigate('OfferDetails', {
        offer: job,
        jobId: job.id,
        source: 'CompanyInfoScreen'
      });
    } catch (error) {
      console.error('❌ Navigation error:', error);
      Alert.alert('Navigation Error', 'Unable to open job details.');
    }
  };

  // Handle share company
  const handleShareCompany = async () => {
    try {
      if (!selectedCompany) return;
      
      const shareContent = {
        message: `Check out ${selectedCompany.name} - ${selectedCompany.description}`,
        url: selectedCompany.website,
        title: `${selectedCompany.name} Company Profile`,
      };
      
      await Share.share(shareContent);
    } catch (error) {
      console.error('❌ Error sharing company:', error);
    }
  };

  // Handle website visit
  const handleWebsiteVisit = async (url) => {
    try {
      await Linking.openURL(url);
    } catch (error) {
      console.error('❌ Error opening website:', error);
      Alert.alert('Error', 'Unable to open website.');
    }
  };

  // Render modern header
  const renderModernHeader = () => (
    <LinearGradient
      colors={selectedCompany ? selectedCompany.gradient : ['#667eea', '#764ba2']}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.modernHeader}
    >
      <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
      
      <View style={styles.modernHeaderContent}>
        <TouchableOpacity
          style={styles.modernBackButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <View style={styles.modernHeaderTitleContainer}>
          <Text style={styles.modernHeaderTitle}>
            {selectedCompany ? selectedCompany.name : 'Companies'}
          </Text>
          {selectedCompany && (
            <Text style={styles.modernHeaderSubtitle}>{selectedCompany.industry}</Text>
          )}
        </View>
        
        {selectedCompany && (
          <TouchableOpacity
            style={styles.modernShareButton}
            onPress={handleShareCompany}
          >
            <Ionicons name="share" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        )}
      </View>
    </LinearGradient>
  );

  // Render modern company list
  const renderModernCompanyList = () => (
    <Animated.View
      style={[
        styles.modernCompanyListContainer,
        {
          opacity: fadeAnim,
          transform: [{ scale: scaleAnim }],
        },
      ]}
    >
      {/* Modern Search Bar */}
      <View style={styles.modernSearchContainer}>
        <LinearGradient
          colors={['#ffffff', '#f8fafc']}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.modernSearchGradient}
        >
          <View style={styles.modernSearchBar}>
            <Ionicons name="search" size={20} color="#64748b" />
            <TextInput
              style={styles.modernSearchInput}
              placeholder="Search companies..."
              placeholderTextColor="#94a3b8"
              value={searchQuery}
              onChangeText={handleSearch}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => handleSearch('')}>
                <Ionicons name="close-circle" size={20} color="#94a3b8" />
              </TouchableOpacity>
            )}
          </View>
        </LinearGradient>
      </View>

      {/* Modern Company List */}
      <FlatList
        data={filteredCompanies}
        keyExtractor={(item) => item.id}
        renderItem={({ item, index }) => (
          <Animated.View
            style={[
              styles.modernCompanyListItemContainer,
              {
                opacity: fadeAnim,
                transform: [
                  { translateY: slideAnim },
                  { scale: scaleAnim }
                ],
              },
            ]}
          >
            <TouchableOpacity
              style={styles.modernCompanyListItem}
              onPress={() => handleCompanySelect(item)}
            >
              <LinearGradient
                colors={['#ffffff', '#f8fafc']}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
                style={styles.modernCompanyListGradient}
              >
                <View style={styles.modernCompanyListContent}>
                  <View style={styles.modernCompanyListLogoContainer}>
                    <LinearGradient
                      colors={item.gradient}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                      style={styles.modernCompanyListLogoBg}
                    >
                      <Image source={{ uri: (item.logo && item.logo.startsWith('http')) ? item.logo : undefined }} style={styles.modernCompanyListLogo} />
                    </LinearGradient>
                    <View style={styles.modernCompanyListRank}>
                      <Text style={styles.modernCompanyListRankText}>{index + 1}</Text>
                    </View>
                  </View>
                  
                  <View style={styles.modernCompanyListInfo}>
                    <Text style={styles.modernCompanyListName}>{item.name}</Text>
                    <Text style={styles.modernCompanyListIndustry}>{item.industry}</Text>
                    <Text style={styles.modernCompanyListLocation}>{item.location}</Text>
                    
                    <View style={styles.modernCompanyListMetrics}>
                      <View style={styles.modernCompanyListRating}>
                        <Ionicons name="star" size={14} color="#fbbf24" />
                        <Text style={styles.modernCompanyListRatingText}>{item.rating}</Text>
                      </View>
                      <View style={styles.modernCompanyListJobs}>
                        <Ionicons name="briefcase" size={14} color="#64748b" />
                        <Text style={styles.modernCompanyListJobsText}>{item.openPositions}</Text>
                      </View>
                    </View>
                  </View>
                  
                  <View style={styles.modernCompanyListArrow}>
                    <Ionicons name="chevron-forward" size={20} color="#cbd5e1" />
                  </View>
                </View>
              </LinearGradient>
            </TouchableOpacity>
          </Animated.View>
        )}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.modernCompanyListContent}
      />
    </Animated.View>
  );

  // Render modern company header
  const renderModernCompanyHeader = () => (
    <Animated.View
      style={[
        styles.modernCompanyHeaderContainer,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <LinearGradient
        colors={companyDetails.gradient}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.modernCompanyHeaderGradient}
      >
        <View style={styles.modernCompanyHeaderContent}>
          <View style={styles.modernCompanyLogoContainer}>
            <View style={styles.modernCompanyLogoBg}>
              <Image source={{ uri: (companyDetails.logo && companyDetails.logo.startsWith('http')) ? companyDetails.logo : undefined }} style={styles.modernCompanyLogo} />
            </View>
            <LinearGradient
              colors={['rgba(255,255,255,0.2)', 'rgba(255,255,255,0.1)']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.modernCompanyLogoOverlay}
            />
          </View>
          
          <View style={styles.modernCompanyInfo}>
            <Text style={styles.modernCompanyName}>{companyDetails.name}</Text>
            <Text style={styles.modernCompanyIndustry}>{companyDetails.industry}</Text>
            <Text style={styles.modernCompanyLocation}>{companyDetails.location}</Text>
            
            <View style={styles.modernCompanyRating}>
              <View style={styles.modernCompanyRatingContainer}>
                <Ionicons name="star" size={16} color="#fbbf24" />
                <Text style={styles.modernCompanyRatingText}>{companyDetails.rating}</Text>
              </View>
              <Text style={styles.modernCompanyEmployees}>• {companyDetails.size}</Text>
            </View>
          </View>
        </View>
      </LinearGradient>
    </Animated.View>
  );

  // Render modern tab selector
  const renderModernTabSelector = () => (
    <View style={styles.modernTabSelectorContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.modernTabSelector}>
        {tabOptions.map((tab, index) => (
          <TouchableOpacity
            key={tab.key}
            style={styles.modernTabWrapper}
            onPress={() => handleTabChange(tab.key)}
          >
            <LinearGradient
              colors={activeTab === tab.key ? tab.gradient : ['transparent', 'transparent']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={[
                styles.modernTabButton,
                activeTab === tab.key && styles.modernTabButtonActive
              ]}
            >
              <Ionicons
                name={tab.icon}
                size={18}
                color={activeTab === tab.key ? '#FFFFFF' : '#64748b'}
              />
              <Text
                style={[
                  styles.modernTabText,
                  activeTab === tab.key && styles.modernTabTextActive
                ]}
              >
                {tab.label}
              </Text>
            </LinearGradient>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  // Render modern overview tab
  const renderModernOverviewTab = () => (
    <Animated.ScrollView
      style={[
        styles.modernTabContent,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
      showsVerticalScrollIndicator={false}
    >
      {/* Modern Company Stats */}
      <View style={styles.modernStatsGrid}>
        <LinearGradient
          colors={['#667eea', '#764ba2']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernStatCard}
        >
          <Ionicons name="people" size={28} color="#FFFFFF" />
          <Text style={styles.modernStatValue}>{companyDetails.employees}</Text>
          <Text style={styles.modernStatLabel}>Employees</Text>
        </LinearGradient>

        <LinearGradient
          colors={['#4facfe', '#00f2fe']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernStatCard}
        >
          <Ionicons name="calendar" size={28} color="#FFFFFF" />
          <Text style={styles.modernStatValue}>{companyDetails.founded}</Text>
          <Text style={styles.modernStatLabel}>Founded</Text>
        </LinearGradient>

        <LinearGradient
          colors={['#43e97b', '#38f9d7']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernStatCard}
        >
          <Ionicons name="briefcase" size={28} color="#FFFFFF" />
          <Text style={styles.modernStatValue}>{companyDetails.openPositions}</Text>
          <Text style={styles.modernStatLabel}>Open Jobs</Text>
        </LinearGradient>

        <LinearGradient
          colors={['#f093fb', '#f5576c']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernStatCard}
        >
          <Ionicons name="star" size={28} color="#FFFFFF" />
          <Text style={styles.modernStatValue}>{companyDetails.rating}</Text>
          <Text style={styles.modernStatLabel}>Rating</Text>
        </LinearGradient>
      </View>

      {/* Modern Description */}
      <View style={styles.modernSectionCard}>
        <LinearGradient
          colors={['#ffffff', '#f8fafc']}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.modernSectionGradient}
        >
          <View style={styles.modernSectionHeader}>
            <Text style={styles.modernSectionTitle}>About {companyDetails.name}</Text>
            <View style={styles.modernSectionIcon}>
              <Ionicons name="information-circle" size={20} color="#667eea" />
            </View>
          </View>
          <Text style={styles.modernSectionText}>{companyDetails.overview}</Text>
        </LinearGradient>
      </View>

      {/* Modern Key Information */}
      <View style={styles.modernSectionCard}>
        <LinearGradient
          colors={['#ffffff', '#f8fafc']}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.modernSectionGradient}
        >
          <View style={styles.modernSectionHeader}>
            <Text style={styles.modernSectionTitle}>Key Information</Text>
            <View style={styles.modernSectionIcon}>
              <Ionicons name="document-text" size={20} color="#667eea" />
            </View>
          </View>
          
          <View style={styles.modernInfoRow}>
            <View style={styles.modernInfoIcon}>
              <Ionicons name="business" size={20} color="#667eea" />
            </View>
            <Text style={styles.modernInfoLabel}>Industry:</Text>
            <Text style={styles.modernInfoValue}>{companyDetails.industry}</Text>
          </View>
          
          <View style={styles.modernInfoRow}>
            <View style={styles.modernInfoIcon}>
              <Ionicons name="location" size={20} color="#667eea" />
            </View>
            <Text style={styles.modernInfoLabel}>Headquarters:</Text>
            <Text style={styles.modernInfoValue}>{companyDetails.headquarters}</Text>
          </View>
          
          <View style={styles.modernInfoRow}>
            <View style={styles.modernInfoIcon}>
              <Ionicons name="cash" size={20} color="#667eea" />
            </View>
            <Text style={styles.modernInfoLabel}>Salary Range:</Text>
            <Text style={styles.modernInfoValue}>{companyDetails.salaryRange}</Text>
          </View>
          
          <View style={styles.modernInfoRow}>
            <View style={styles.modernInfoIcon}>
              <Ionicons name="globe" size={20} color="#667eea" />
            </View>
            <Text style={styles.modernInfoLabel}>Website:</Text>
            <TouchableOpacity onPress={() => handleWebsiteVisit(companyDetails.website)}>
              <Text style={styles.modernInfoLink}>Visit Website</Text>
            </TouchableOpacity>
          </View>
        </LinearGradient>
      </View>

      {/* Modern Technologies */}
      {companyDetails.technologies && (
        <View style={styles.modernSectionCard}>
          <LinearGradient
            colors={['#ffffff', '#f8fafc']}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={styles.modernSectionGradient}
          >
            <View style={styles.modernSectionHeader}>
              <Text style={styles.modernSectionTitle}>Technologies</Text>
              <View style={styles.modernSectionIcon}>
                <Ionicons name="code-slash" size={20} color="#667eea" />
              </View>
            </View>
            <View style={styles.modernTechGrid}>
              {companyDetails.technologies.map((tech, index) => (
                <LinearGradient
                  key={index}
                  colors={['#667eea', '#764ba2']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.modernTechChip}
                >
                  <Text style={styles.modernTechText}>{tech}</Text>
                </LinearGradient>
              ))}
            </View>
          </LinearGradient>
        </View>
      )}

      {/* Modern Values */}
      {companyDetails.values && (
        <View style={styles.modernSectionCard}>
          <LinearGradient
            colors={['#667eea', '#764ba2']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.modernValuesGradient}
          >
            <View style={styles.modernValuesHeader}>
              <View style={styles.modernValuesIcon}>
                <Ionicons name="heart" size={24} color="#FFFFFF" />
              </View>
              <Text style={styles.modernValuesTitle}>Company Values</Text>
            </View>
            {companyDetails.values.map((value, index) => (
              <View key={index} style={styles.modernValueItem}>
                <View style={styles.modernValueIcon}>
                  <Ionicons name="checkmark-circle" size={16} color="#FFFFFF" />
                </View>
                <Text style={styles.modernValueText}>{value}</Text>
              </View>
            ))}
          </LinearGradient>
        </View>
      )}
    </Animated.ScrollView>
  );

  // Render modern jobs tab
  const renderModernJobsTab = () => (
    <Animated.ScrollView
      style={[
        styles.modernTabContent,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.modernJobsHeader}>
        <Text style={styles.modernJobsTitle}>Open Positions ({relatedJobs.length})</Text>
        <View style={styles.modernJobsIcon}>
          <Ionicons name="briefcase" size={20} color="#667eea" />
        </View>
      </View>
      
      {relatedJobs.length > 0 ? (
        relatedJobs.map((job, index) => (
          <TouchableOpacity
            key={index}
            style={styles.modernJobCard}
            onPress={() => handleJobApplication(job)}
          >
            <LinearGradient
              colors={['#ffffff', '#f8fafc']}
              start={{ x: 0, y: 0 }}
              end={{ x: 0, y: 1 }}
              style={styles.modernJobGradient}
            >
              <View style={styles.modernJobHeader}>
                <Text style={styles.modernJobTitle}>{job.title}</Text>
                <LinearGradient
                  colors={['#43e97b', '#38f9d7']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.modernJobSalaryBg}
                >
                  <Text style={styles.modernJobSalary}>{job.salary}</Text>
                </LinearGradient>
              </View>
              
              <View style={styles.modernJobMeta}>
                <View style={styles.modernJobMetaItem}>
                  <Ionicons name="location" size={14} color="#64748b" />
                  <Text style={styles.modernJobLocation}>{job.location}</Text>
                </View>
                <View style={styles.modernJobMetaItem}>
                  <Ionicons name="time" size={14} color="#64748b" />
                  <Text style={styles.modernJobType}>{job.type}</Text>
                </View>
              </View>
              
              <Text style={styles.modernJobDescription} numberOfLines={2}>
                {job.description}
              </Text>
              
              <View style={styles.modernJobFooter}>
                <LinearGradient
                  colors={['#667eea', '#764ba2']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.modernJobMatch}
                >
                  <Text style={styles.modernJobMatchText}>{job.matchScore}% match</Text>
                </LinearGradient>
                <Text style={styles.modernJobDate}>Posted {job.postedDate}</Text>
              </View>
            </LinearGradient>
          </TouchableOpacity>
        ))
      ) : (
        <View style={styles.modernEmptyState}>
          <LinearGradient
            colors={['#f8fafc', '#f1f5f9']}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={styles.modernEmptyStateGradient}
          >
            <Ionicons name="briefcase-outline" size={48} color="#94a3b8" />
            <Text style={styles.modernEmptyStateText}>No open positions available</Text>
            <Text style={styles.modernEmptyStateSubtext}>Check back later for new opportunities</Text>
          </LinearGradient>
        </View>
      )}
    </Animated.ScrollView>
  );

  // Render modern analytics tab
  const renderModernAnalyticsTab = () => {
    const analytics = companyAnalytics[companyDetails?.name] || {};
    
    return (
      <Animated.ScrollView
        style={[
          styles.modernTabContent,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
        showsVerticalScrollIndicator={false}
      >
        {/* Modern Application Analytics */}
        <View style={styles.modernAnalyticsCard}>
          <LinearGradient
            colors={['#667eea', '#764ba2']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.modernAnalyticsGradient}
          >
            <View style={styles.modernAnalyticsHeader}>
              <View style={styles.modernAnalyticsIcon}>
                <Ionicons name="analytics" size={24} color="#FFFFFF" />
              </View>
              <Text style={styles.modernAnalyticsTitle}>Your Application Analytics</Text>
            </View>
            
            <View style={styles.modernAnalyticsGrid}>
              <View style={styles.modernAnalyticsItem}>
                <Text style={styles.modernAnalyticsValue}>{analytics.totalApplications || 0}</Text>
                <Text style={styles.modernAnalyticsLabel}>Applications</Text>
              </View>
              <View style={styles.modernAnalyticsItem}>
                <Text style={styles.modernAnalyticsValue}>{analytics.interviews || 0}</Text>
                <Text style={styles.modernAnalyticsLabel}>Interviews</Text>
              </View>
              <View style={styles.modernAnalyticsItem}>
                <Text style={styles.modernAnalyticsValue}>{analytics.offers || 0}</Text>
                <Text style={styles.modernAnalyticsLabel}>Offers</Text>
              </View>
              <View style={styles.modernAnalyticsItem}>
                <Text style={styles.modernAnalyticsValue}>{analytics.successRate || 0}%</Text>
                <Text style={styles.modernAnalyticsLabel}>Success Rate</Text>
              </View>
            </View>
          </LinearGradient>
        </View>

        {/* Modern Performance Metrics */}
        <View style={styles.modernPerformanceCard}>
          <LinearGradient
            colors={['#ffffff', '#f8fafc']}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={styles.modernPerformanceGradient}
          >
            <View style={styles.modernPerformanceHeader}>
              <Text style={styles.modernPerformanceTitle}>Performance Metrics</Text>
              <View style={styles.modernPerformanceIcon}>
                <Ionicons name="trending-up" size={20} color="#667eea" />
              </View>
            </View>
            
            <View style={styles.modernMetricRow}>
              <Text style={styles.modernMetricLabel}>Average Match Score:</Text>
              <LinearGradient
                colors={['#43e97b', '#38f9d7']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.modernMetricValueBg}
              >
                <Text style={styles.modernMetricValue}>{analytics.avgMatchScore || 0}%</Text>
              </LinearGradient>
            </View>
            
            <View style={styles.modernMetricRow}>
              <Text style={styles.modernMetricLabel}>Response Rate:</Text>
              <LinearGradient
                colors={['#4facfe', '#00f2fe']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.modernMetricValueBg}
              >
                <Text style={styles.modernMetricValue}>{analytics.responseRate || 0}%</Text>
              </LinearGradient>
            </View>
            
            <View style={styles.modernMetricRow}>
              <Text style={styles.modernMetricLabel}>Positions Applied:</Text>
              <LinearGradient
                colors={['#f093fb', '#f5576c']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.modernMetricValueBg}
              >
                <Text style={styles.modernMetricValue}>{analytics.positions?.length || 0}</Text>
              </LinearGradient>
            </View>
          </LinearGradient>
        </View>

        {/* Modern Salary Insights */}
        {analytics.salaryRange && analytics.salaryRange.min && (
          <View style={styles.modernSalaryCard}>
            <LinearGradient
              colors={['#43e97b', '#38f9d7']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.modernSalaryGradient}
            >
              <View style={styles.modernSalaryHeader}>
                <View style={styles.modernSalaryIcon}>
                  <Ionicons name="cash" size={24} color="#FFFFFF" />
                </View>
                <Text style={styles.modernSalaryTitle}>Salary Insights</Text>
              </View>
              <Text style={styles.modernSalaryRange}>
                £{analytics.salaryRange.min?.toLocaleString()} - £{analytics.salaryRange.max?.toLocaleString()}
              </Text>
              <Text style={styles.modernSalaryNote}>Based on your applications to this company</Text>
            </LinearGradient>
          </View>
        )}
      </Animated.ScrollView>
    );
  };

  // Render modern reviews tab
  const renderModernReviewsTab = () => (
    <Animated.ScrollView
      style={[
        styles.modernTabContent,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.modernReviewsHeader}>
        <LinearGradient
          colors={['#f093fb', '#f5576c']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernReviewsHeaderGradient}
        >
          <View style={styles.modernReviewsHeaderContent}>
            <Text style={styles.modernReviewsTitle}>Employee Reviews</Text>
            <View style={styles.modernOverallRating}>
              <Text style={styles.modernRatingValue}>{companyDetails.rating}</Text>
              <View style={styles.modernRatingStars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Ionicons
                    key={star}
                    name={star <= Math.floor(companyDetails.rating) ? 'star' : 'star-outline'}
                    size={16}
                    color="#FFFFFF"
                  />
                ))}
              </View>
              <Text style={styles.modernRatingCount}>({companyDetails.reviews?.length || 0} reviews)</Text>
            </View>
          </View>
        </LinearGradient>
      </View>

      {companyDetails.reviews?.map((review, index) => (
        <View key={index} style={styles.modernReviewCard}>
          <LinearGradient
            colors={['#ffffff', '#f8fafc']}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={styles.modernReviewGradient}
          >
            <View style={styles.modernReviewHeader}>
              <View style={styles.modernReviewRating}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Ionicons
                    key={star}
                    name={star <= review.rating ? 'star' : 'star-outline'}
                    size={14}
                    color="#fbbf24"
                  />
                ))}
              </View>
              <Text style={styles.modernReviewDate}>{review.date}</Text>
            </View>
            
            <Text style={styles.modernReviewTitle}>{review.title}</Text>
            <Text style={styles.modernReviewPosition}>{review.position}</Text>
            <Text style={styles.modernReviewComment}>{review.comment}</Text>
            
            {review.pros && (
              <View style={styles.modernProsConsSection}>
                <View style={styles.modernProsConsHeader}>
                  <Ionicons name="thumbs-up" size={16} color="#10b981" />
                  <Text style={styles.modernProsConsTitle}>Pros:</Text>
                </View>
                {review.pros.map((pro, idx) => (
                  <Text key={idx} style={styles.modernProsConsItem}>• {pro}</Text>
                ))}
              </View>
            )}
            
            {review.cons && (
              <View style={styles.modernProsConsSection}>
                <View style={styles.modernProsConsHeader}>
                  <Ionicons name="thumbs-down" size={16} color="#ef4444" />
                  <Text style={styles.modernProsConsTitle}>Cons:</Text>
                </View>
                {review.cons.map((con, idx) => (
                  <Text key={idx} style={styles.modernProsConsItem}>• {con}</Text>
                ))}
              </View>
            )}
          </LinearGradient>
        </View>
      ))}
    </Animated.ScrollView>
  );

  // Render modern culture tab
  const renderModernCultureTab = () => (
    <Animated.ScrollView
      style={[
        styles.modernTabContent,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
      showsVerticalScrollIndicator={false}
    >
      {/* Modern Culture Overview */}
      <View style={styles.modernCultureCard}>
        <LinearGradient
          colors={['#ffecd2', '#fcb69f']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernCultureGradient}
        >
          <View style={styles.modernCultureHeader}>
            <View style={styles.modernCultureIcon}>
              <Ionicons name="people" size={24} color="#FFFFFF" />
            </View>
            <Text style={styles.modernCultureTitle}>Company Culture</Text>
          </View>
          <Text style={styles.modernCultureText}>{companyDetails.culture}</Text>
        </LinearGradient>
      </View>

      {/* Modern Benefits */}
      <View style={styles.modernBenefitsCard}>
        <LinearGradient
          colors={['#ffffff', '#f8fafc']}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.modernBenefitsGradient}
        >
          <View style={styles.modernBenefitsHeader}>
            <Text style={styles.modernBenefitsTitle}>Benefits & Perks</Text>
            <View style={styles.modernBenefitsIcon}>
              <Ionicons name="gift" size={20} color="#667eea" />
            </View>
          </View>
          
          {companyDetails.benefits?.map((benefit, index) => (
            <View key={index} style={styles.modernBenefitItem}>
              <LinearGradient
                colors={['#43e97b', '#38f9d7']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.modernBenefitIcon}
              >
                <Ionicons name="checkmark" size={12} color="#FFFFFF" />
              </LinearGradient>
              <Text style={styles.modernBenefitText}>{benefit}</Text>
            </View>
          ))}
        </LinearGradient>
      </View>

      {/* Modern Work Environment */}
      <View style={styles.modernEnvironmentCard}>
        <LinearGradient
          colors={['#667eea', '#764ba2']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernEnvironmentGradient}
        >
          <Text style={styles.modernEnvironmentTitle}>Work Environment</Text>
          <View style={styles.modernEnvironmentGrid}>
            <View style={styles.modernEnvironmentItem}>
              <View style={styles.modernEnvironmentIcon}>
                <Ionicons name="home" size={24} color="#FFFFFF" />
              </View>
              <Text style={styles.modernEnvironmentLabel}>Remote Work</Text>
              <Text style={styles.modernEnvironmentValue}>Available</Text>
            </View>
            
            <View style={styles.modernEnvironmentItem}>
              <View style={styles.modernEnvironmentIcon}>
                <Ionicons name="time" size={24} color="#FFFFFF" />
              </View>
              <Text style={styles.modernEnvironmentLabel}>Flexible Hours</Text>
              <Text style={styles.modernEnvironmentValue}>Yes</Text>
            </View>
            
            <View style={styles.modernEnvironmentItem}>
              <View style={styles.modernEnvironmentIcon}>
                <Ionicons name="people" size={24} color="#FFFFFF" />
              </View>
              <Text style={styles.modernEnvironmentLabel}>Team Size</Text>
              <Text style={styles.modernEnvironmentValue}>10-50</Text>
            </View>
            
            <View style={styles.modernEnvironmentItem}>
              <View style={styles.modernEnvironmentIcon}>
                <Ionicons name="school" size={24} color="#FFFFFF" />
              </View>
              <Text style={styles.modernEnvironmentLabel}>Learning</Text>
              <Text style={styles.modernEnvironmentValue}>Encouraged</Text>
            </View>
          </View>
        </LinearGradient>
      </View>
    </Animated.ScrollView>
  );

  // Render current tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderModernOverviewTab();
      case 'jobs':
        return renderModernJobsTab();
      case 'analytics':
        return renderModernAnalyticsTab();
      case 'reviews':
        return renderModernReviewsTab();
      case 'culture':
        return renderModernCultureTab();
      default:
        return renderModernOverviewTab();
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <LinearGradient
          colors={['#667eea', '#764ba2']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modernLoadingContainer}
        >
          <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
          <View style={styles.modernLoadingContent}>
            <Animated.View
              style={{
                transform: [{
                  rotate: rotateAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: ['0deg', '360deg'],
                  }),
                }],
              }}
            >
              <Ionicons name="business" size={48} color="#FFFFFF" />
            </Animated.View>
            <Text style={styles.modernLoadingText}>Loading Company Info...</Text>
            <Text style={styles.modernLoadingSubtext}>Preparing company details</Text>
          </View>
        </LinearGradient>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Modern Header */}
      {renderModernHeader()}

      {!selectedCompany ? (
        renderModernCompanyList()
      ) : (
        <View style={styles.modernCompanyDetailContainer}>
          {/* Modern Company Header */}
          {renderModernCompanyHeader()}

          {/* Modern Tab Selector */}
          {renderModernTabSelector()}

          {/* Tab Content */}
          {renderTabContent()}
        </View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  modernLoadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernLoadingContent: {
    alignItems: 'center',
  },
  modernLoadingText: {
    marginTop: 24,
    fontSize: 20,
    color: '#FFFFFF',
    fontWeight: '600',
  },
  modernLoadingSubtext: {
    marginTop: 8,
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.8)',
    fontWeight: '400',
  },
  modernHeader: {
    paddingTop: Platform.OS === 'ios' ? 44 : StatusBar.currentHeight,
    paddingBottom: 20,
  },
  modernHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 16,
  },
  modernBackButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    backdropFilter: 'blur(10px)',
  },
  modernHeaderTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  modernHeaderTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
    letterSpacing: 0.5,
    textAlign: 'center',
  },
  modernHeaderSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    fontWeight: '400',
    marginTop: 2,
  },
  modernShareButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    backdropFilter: 'blur(10px)',
  },
  modernCompanyListContainer: {
    flex: 1,
  },
  modernSearchContainer: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  modernSearchGradient: {
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 6,
  },
  modernSearchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  modernSearchInput: {
    flex: 1,
    marginLeft: 12,
    fontSize: 16,
    color: '#1e293b',
    fontWeight: '500',
  },
  modernCompanyListItemContainer: {
    marginHorizontal: 20,
    marginVertical: 8,
  },
  modernCompanyListItem: {
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 8,
  },
  modernCompanyListGradient: {
    padding: 20,
  },
  modernCompanyListContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  modernCompanyListLogoContainer: {
    position: 'relative',
    marginRight: 16,
  },
  modernCompanyListLogoBg: {
    width: 60,
    height: 60,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernCompanyListLogo: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  modernCompanyListRank: {
    position: 'absolute',
    top: -6,
    right: -6,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#1e293b',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernCompanyListRankText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  modernCompanyListInfo: {
    flex: 1,
  },
  modernCompanyListName: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: 4,
  },
  modernCompanyListIndustry: {
    fontSize: 14,
    color: '#64748b',
    marginBottom: 2,
  },
  modernCompanyListLocation: {
    fontSize: 12,
    color: '#94a3b8',
    marginBottom: 8,
  },
  modernCompanyListMetrics: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  modernCompanyListRating: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
  modernCompanyListRatingText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1e293b',
    marginLeft: 4,
  },
  modernCompanyListJobs: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  modernCompanyListJobsText: {
    fontSize: 12,
    color: '#64748b',
    marginLeft: 4,
  },
  modernCompanyListArrow: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f8fafc',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  modernCompanyDetailContainer: {
    flex: 1,
  },
  modernCompanyHeaderContainer: {
    marginHorizontal: 20,
    marginTop: 20,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 10,
  },
  modernCompanyHeaderGradient: {
    padding: 24,
  },
  modernCompanyHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  modernCompanyLogoContainer: {
    position: 'relative',
    marginRight: 20,
  },
  modernCompanyLogoBg: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernCompanyLogo: {
    width: 60,
    height: 60,
    borderRadius: 30,
  },
  modernCompanyLogoOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 40,
  },
  modernCompanyInfo: {
    flex: 1,
  },
  modernCompanyName: {
    fontSize: 24,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 4,
  },
  modernCompanyIndustry: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: 2,
  },
  modernCompanyLocation: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: 12,
  },
  modernCompanyRating: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  modernCompanyRatingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  modernCompanyRatingText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  modernCompanyEmployees: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginLeft: 8,
  },
  modernTabSelectorContainer: {
    backgroundColor: '#FFFFFF',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
    marginTop: 20,
  },
  modernTabSelector: {
    paddingHorizontal: 20,
  },
  modernTabWrapper: {
    marginRight: 12,
  },
  modernTabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 25,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  modernTabButtonActive: {
    borderColor: 'transparent',
    shadowColor: '#667eea',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
  },
  modernTabText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#64748b',
  },
  modernTabTextActive: {
    color: '#FFFFFF',
  },
  modernTabContent: {
    flex: 1,
    padding: 20,
  },
  modernStatsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 24,
    justifyContent: 'space-between',
  },
  modernStatCard: {
    width: (width - 60) / 2,
    borderRadius: 20,
    padding: 20,
    alignItems: 'center',
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 10,
  },
  modernStatValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#FFFFFF',
    marginTop: 12,
    marginBottom: 8,
  },
  modernStatLabel: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    fontWeight: '500',
  },
  modernSectionCard: {
    marginBottom: 24,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 8,
  },
  modernSectionGradient: {
    padding: 24,
  },
  modernSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modernSectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1e293b',
  },
  modernSectionIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f1f5f9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernSectionText: {
    fontSize: 16,
    color: '#64748b',
    lineHeight: 24,
  },
  modernInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  modernInfoIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f1f5f9',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  modernInfoLabel: {
    fontSize: 14,
    color: '#64748b',
    marginRight: 8,
    minWidth: 80,
    fontWeight: '500',
  },
  modernInfoValue: {
    fontSize: 14,
    color: '#1e293b',
    fontWeight: '600',
    flex: 1,
  },
  modernInfoLink: {
    fontSize: 14,
    color: '#667eea',
    fontWeight: '600',
    flex: 1,
  },
  modernTechGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  modernTechChip: {
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginRight: 8,
    marginBottom: 8,
  },
  modernTechText: {
    fontSize: 12,
    color: '#FFFFFF',
    fontWeight: '600',
  },
  modernValuesGradient: {
    padding: 24,
  },
  modernValuesHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  modernValuesIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  modernValuesTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  modernValueItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    padding: 12,
  },
  modernValueIcon: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  modernValueText: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    flex: 1,
    lineHeight: 20,
  },
  modernJobsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modernJobsTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1e293b',
  },
  modernJobsIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f1f5f9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernJobCard: {
    marginBottom: 16,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 8,
  },
  modernJobGradient: {
    padding: 20,
  },
  modernJobHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  modernJobTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
    flex: 1,
    marginRight: 12,
  },
  modernJobSalaryBg: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  modernJobSalary: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  modernJobMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  modernJobMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
  modernJobLocation: {
    fontSize: 14,
    color: '#64748b',
    marginLeft: 4,
  },
  modernJobType: {
    fontSize: 14,
    color: '#64748b',
    marginLeft: 4,
  },
  modernJobDescription: {
    fontSize: 14,
    color: '#64748b',
    lineHeight: 20,
    marginBottom: 16,
  },
  modernJobFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modernJobMatch: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  modernJobMatchText: {
    fontSize: 12,
    color: '#FFFFFF',
    fontWeight: '700',
  },
  modernJobDate: {
    fontSize: 12,
    color: '#94a3b8',
  },
  modernEmptyState: {
    marginTop: 40,
    borderRadius: 20,
    overflow: 'hidden',
  },
  modernEmptyStateGradient: {
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: 40,
  },
  modernEmptyStateText: {
    fontSize: 18,
    color: '#64748b',
    marginTop: 16,
    fontWeight: '600',
  },
  modernEmptyStateSubtext: {
    fontSize: 14,
    color: '#94a3b8',
    marginTop: 8,
    textAlign: 'center',
  },
  modernAnalyticsCard: {
    marginBottom: 24,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 10,
  },
  modernAnalyticsGradient: {
    padding: 24,
  },
  modernAnalyticsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  modernAnalyticsIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  modernAnalyticsTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  modernAnalyticsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  modernAnalyticsItem: {
    alignItems: 'center',
    flex: 1,
  },
  modernAnalyticsValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 8,
  },
  modernAnalyticsLabel: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    fontWeight: '500',
  },
  modernPerformanceCard: {
    marginBottom: 24,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 8,
  },
  modernPerformanceGradient: {
    padding: 24,
  },
  modernPerformanceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modernPerformanceTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1e293b',
  },
  modernPerformanceIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f1f5f9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernMetricRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modernMetricLabel: {
    fontSize: 16,
    color: '#64748b',
    fontWeight: '500',
  },
  modernMetricValueBg: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  modernMetricValue: {
    fontSize: 16,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  modernSalaryCard: {
    marginBottom: 24,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 10,
  },
  modernSalaryGradient: {
    padding: 24,
  },
  modernSalaryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  modernSalaryIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  modernSalaryTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  modernSalaryRange: {
    fontSize: 28,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 8,
  },
  modernSalaryNote: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  modernReviewsHeader: {
    marginBottom: 24,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 10,
  },
  modernReviewsHeaderGradient: {
    padding: 24,
  },
  modernReviewsHeaderContent: {
    alignItems: 'center',
  },
  modernReviewsTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 16,
  },
  modernOverallRating: {
    alignItems: 'center',
  },
  modernRatingValue: {
    fontSize: 32,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 8,
  },
  modernRatingStars: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  modernRatingCount: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  modernReviewCard: {
    marginBottom: 16,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 8,
  },
  modernReviewGradient: {
    padding: 20,
  },
  modernReviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  modernReviewRating: {
    flexDirection: 'row',
  },
  modernReviewDate: {
    fontSize: 12,
    color: '#94a3b8',
  },
  modernReviewTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: 4,
  },
  modernReviewPosition: {
    fontSize: 14,
    color: '#64748b',
    marginBottom: 12,
  },
  modernReviewComment: {
    fontSize: 14,
    color: '#64748b',
    lineHeight: 20,
    marginBottom: 16,
  },
  modernProsConsSection: {
    marginBottom: 12,
  },
  modernProsConsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  modernProsConsTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1e293b',
    marginLeft: 8,
  },
  modernProsConsItem: {
    fontSize: 12,
    color: '#64748b',
    marginBottom: 4,
    marginLeft: 24,
  },
  modernCultureCard: {
    marginBottom: 24,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 10,
  },
  modernCultureGradient: {
    padding: 24,
  },
  modernCultureHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  modernCultureIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  modernCultureTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  modernCultureText: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
    lineHeight: 24,
  },
  modernBenefitsCard: {
    marginBottom: 24,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 8,
  },
  modernBenefitsGradient: {
    padding: 24,
  },
  modernBenefitsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modernBenefitsTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1e293b',
  },
  modernBenefitsIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f1f5f9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernBenefitItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  modernBenefitIcon: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  modernBenefitText: {
    fontSize: 14,
    color: '#64748b',
    flex: 1,
    lineHeight: 20,
  },
  modernEnvironmentCard: {
    marginBottom: 24,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 10,
  },
  modernEnvironmentGradient: {
    padding: 24,
  },
  modernEnvironmentTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 20,
    textAlign: 'center',
  },
  modernEnvironmentGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  modernEnvironmentItem: {
    width: (width - 80) / 2,
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
  },
  modernEnvironmentIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  modernEnvironmentLabel: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: 4,
    textAlign: 'center',
    fontWeight: '500',
  },
  modernEnvironmentValue: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
    textAlign: 'center',
  },
});
